import React, { useState } from 'react';
import { getImageUrl } from '../../utils/imageUrl';

interface CarouselProps {
  items: Array<{ id: string; image_url: string; label?: string }>;
  selectedId?: string;
  onSelect?: (id: string) => void;
  className?: string;
}

export const Carousel: React.FC<CarouselProps> = ({
  items,
  selectedId,
  onSelect,
  className = '',
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  if (!items || items.length === 0) {
    return (
      <div className={`text-center py-8 text-gray-400 ${className}`}>
        <p>No items available</p>
      </div>
    );
  }

  const goToPrevious = () => {
    setCurrentIndex((prev) => {
      const newIndex = prev === 0 ? items.length - 1 : prev - 1;
      // Auto-select the item being viewed
      if (onSelect) {
        onSelect(items[newIndex].id);
      }
      return newIndex;
    });
  };

  const goToNext = () => {
    setCurrentIndex((prev) => {
      const newIndex = prev === items.length - 1 ? 0 : prev + 1;
      // Auto-select the item being viewed
      if (onSelect) {
        onSelect(items[newIndex].id);
      }
      return newIndex;
    });
  };

  const goToIndex = (index: number) => {
    setCurrentIndex(index);
  };

  const handleItemClick = (id: string) => {
    if (onSelect) {
      onSelect(id);
    }
  };

  return (
    <div className={`relative ${className}`}>
      {/* Main image display */}
      <div className="relative h-48 rounded-xl overflow-hidden bg-gray-100 border-2 border-gray-200">
        <img
          src={getImageUrl(items[currentIndex].image_url)}
          alt={items[currentIndex].label || 'Item'}
          className="w-full h-full object-cover"
          onError={(e) => {
            console.error(`Failed to load image: ${items[currentIndex].image_url}`);
            const target = e.target as HTMLImageElement;
            target.style.display = 'none';
          }}
        />
        
        {/* Navigation arrows */}
        {items.length > 1 && (
          <>
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                goToPrevious();
              }}
              className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-2 transition-colors z-10"
              aria-label="Previous"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                goToNext();
              }}
              className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-2 transition-colors z-10"
              aria-label="Next"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </>
        )}

        {/* Selected indicator */}
        {selectedId === items[currentIndex].id && (
          <div className="absolute top-2 right-2 bg-primary rounded-full w-8 h-8 flex items-center justify-center">
            <span className="text-black text-sm font-bold">✓</span>
          </div>
        )}
      </div>

      {/* Thumbnail strip */}
      {items.length > 1 && (
        <div className="mt-3 flex gap-2 overflow-x-auto pb-2">
          {items.map((item, index) => (
            <button
              key={item.id}
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                goToIndex(index);
                handleItemClick(item.id);
              }}
              className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition-all ${
                index === currentIndex
                  ? 'border-primary ring-2 ring-primary ring-offset-2'
                  : selectedId === item.id
                  ? 'border-primary'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <img
                src={getImageUrl(item.image_url)}
                alt={item.label || 'Thumbnail'}
                className="w-full h-full object-cover"
              />
            </button>
          ))}
        </div>
      )}

      {/* Item info */}
      {items[currentIndex].label && (
        <p className="mt-2 text-sm text-gray-600 text-center">{items[currentIndex].label}</p>
      )}

      {/* Counter */}
      {items.length > 1 && (
        <p className="mt-1 text-xs text-gray-400 text-center">
          {currentIndex + 1} / {items.length}
        </p>
      )}
    </div>
  );
};

